import Markdown from "markdown-to-jsx";

import type { CardSource } from "@/components/chat/chatCardEvents";
import ChatCommerceCard from "@/components/chat/ChatCommerceCard";
import ChatConfidence from "@/components/chat/ChatConfidence";
import ChatCTA from "@/components/chat/ChatCTA";
import ChatFeedback from "@/components/chat/ChatFeedback";
import ChatProgramCard from "@/components/chat/ChatProgramCard";
import ChatSavedCommerceCard from "@/components/chat/ChatSavedCommerceCard";
import ChatSourceCards from "@/components/chat/ChatSourceCards";
import type { RefreshedCards } from "@/hooks/useSavedCardRefresh";
import type {
  ContentBlock,
  ResponseMetadata,
  SourceItem,
} from "@/services/aiService";
import {
  decodeLiveCard,
  decodeSavedBlockOffer,
  type CommerceCardBlock,
} from "@/services/commerceOffers";
import type { ProgramCardBlock } from "@/services/commerceProgramOffers";

interface Props {
  role: "user" | "assistant";
  content: string;
  blocks?: ContentBlock[];
  messageIndex: number;
  sessionId: string;
  refreshedCards?: RefreshedCards;
  // A past conversation's cards, or a cut turn's recovered cards in the live chat.
  refreshedSource?: "live" | "history";
  metadata?: ResponseMetadata | null;
  isStaff?: boolean;
  isStreaming?: boolean;
  onExpandSources?: (sources: SourceItem[]) => void;
}

export default function ChatMessage({
  role,
  content,
  blocks,
  messageIndex,
  sessionId,
  refreshedCards,
  refreshedSource = "history",
  metadata,
  isStaff = false,
  isStreaming = false,
  onExpandSources,
}: Props) {
  const isUser = role === "user";

  if (isUser) {
    return (
      <div className="flex justify-end min-h-[40px]">
        <div className="bg-[var(--chat-creme)] px-[var(--chat-space-06)] py-[var(--chat-space-05)] rounded-[var(--chat-corner-base)] max-w-[var(--chat-max-bubble)]">
          <p className="text-[var(--chat-text-base)] font-medium leading-[1.5] text-[var(--chat-black)]">
            {content}
          </p>
        </div>
      </div>
    );
  }

  const sourcesBlock = blocks?.find(
    (b): b is Extract<ContentBlock, { type: "sources" }> => b.type === "sources"
  );

  // A live card by its offer's kind: this answer's, or a restored card's refresh.
  const liveCard = (
    card: CommerceCardBlock | ProgramCardBlock,
    cardSessionId: string,
    source: CardSource
  ) => {
    const { offer } = card;
    // Card state belongs to its offer, not its position in the list.
    const key = `offer:${offer.offer_id}`;
    return offer.kind === "program" ? (
      <ChatProgramCard
        key={key}
        offer={offer}
        sessionId={cardSessionId}
        messageIndex={messageIndex}
        source={source}
      />
    ) : (
      <ChatCommerceCard
        key={key}
        // Rebuilt with the narrowed offer, so the type holds without a cast.
        card={{ ...card, offer }}
        sessionId={cardSessionId}
        messageIndex={messageIndex}
        source={source}
      />
    );
  };

  const renderContent = () => {
    if (!blocks || blocks.length === 0) {
      return (
        <Markdown
          options={{ forceBlock: true, disableParsingRawHTML: true }}
          className="text-[var(--chat-text-base)] leading-[1.5] text-[var(--chat-black)] w-full prose prose-sm max-w-none"
        >
          {content}
        </Markdown>
      );
    }

    return blocks.map((block, i) => {
      switch (block.type) {
        case "text":
          return (
            <Markdown
              key={i}
              options={{ forceBlock: true, disableParsingRawHTML: true }}
              className="text-[var(--chat-text-base)] leading-[1.5] text-[var(--chat-black)] w-full prose prose-sm max-w-none"
            >
              {block.content}
            </Markdown>
          );
        case "product":
          // Retired hardcoded cards the AI still streams in live answers (Boric Acid,
          // The First Line, Vaginal Health Test) until evvy-ai A10 ships: stale prices
          // and links, so hidden for every member, commerce switch on or off.
          return null;
        case "cta":
          return <ChatCTA key={i} label={block.label} action={block.action} />;
        case "confidence":
          return <ChatConfidence key={i} level={block.level} />;
        case "sources":
          return <ChatSourceCards key={i} sources={block.items} />;
        case "commerce_card": {
          // Decoded here too, so a block from any source is live only when complete.
          const card = decodeLiveCard(block);
          return card && liveCard(card, sessionId, "live");
        }
        case "saved_commerce_card":
        case "saved_program_card": {
          // Restored from history: read-only until a refresh returns its offer_id, same kind.
          const offer = decodeSavedBlockOffer(block);
          if (!offer) return null;
          const kind =
            block.type === "saved_program_card" ? "program" : "treatment";
          const card = refreshedCards?.cards.get(offer.offer_id);
          if (refreshedCards && card?.offer.kind === kind)
            // Its own key, so its tap state never mixes with the live chat's cards.
            return liveCard(card, refreshedCards.sessionId, refreshedSource);
          return (
            <ChatSavedCommerceCard
              key={`offer:${offer.offer_id}`}
              offer={offer}
              withdrawn={!!refreshedCards?.withdrawn.has(offer.offer_id)}
              inCart={refreshedCards?.inCart?.has(offer.offer_id)}
            />
          );
        }
        default:
          return null;
      }
    });
  };

  return (
    <div className="flex flex-col items-start gap-[var(--chat-space-06)] w-full max-w-[var(--chat-max-bubble)]">
      {renderContent()}

      {isStreaming && (
        <span className="inline-block w-1.5 h-4 bg-[var(--chat-black)] animate-pulse" />
      )}

      {!isStreaming && (
        <ChatFeedback
          sessionId={sessionId}
          traceId={metadata?.trace_id ?? null}
          content={content}
          sources={sourcesBlock?.items}
          onExpandSources={onExpandSources}
        />
      )}
    </div>
  );
}
